import { Injectable } from '@angular/core';
import { Role, ROLE_ALIASES } from '@angular-workspace/services';

/**
 * Format Service
 * Provides utility methods for formatting data (dates, numbers, strings, etc.)
 */
@Injectable({
  providedIn: 'root',
})
export class FormatService {
  /** IST timezone (Asia/Kolkata, UTC+5:30) */
  private static readonly IST_TIMEZONE = 'Asia/Kolkata';

  /**
   * Normalizes ISO 8601 strings without timezone (e.g. "2026-02-03T11:09:22.330111")
   * to UTC by appending 'Z'. API responses often send UTC timestamps without the Z suffix;
   * JS would otherwise parse them as local time.
   */
  private static normalizeToUtc(dateString: string): string {
    const s = dateString.trim();
    if (!s) return s;
    const hasTimezone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(s);
    if (hasTimezone) return s.replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s)) {
      return s.replace(' ', 'T').replace(/\.\d+$/, '') + 'Z';
    }
    return s;
  }

  /** Parses API timestamps (ISO strings, epoch ms/s, numeric strings) for IST display. */
  private static parseApiTimestamp(
    value: string | number,
    assumeIstWhenNoTimezone = false,
  ): Date | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'number' && !Number.isNaN(value)) {
      const ms = value > 0 && value < 1e12 ? value * 1000 : value;
      const date = new Date(ms);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const s = String(value).trim();
    if (!s) {
      return null;
    }
    if (/^\d+$/.test(s)) {
      const n = Number(s);
      const ms = n > 0 && n < 1e12 ? n * 1000 : n;
      const date = new Date(ms);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const normalized = assumeIstWhenNoTimezone
      ? FormatService.normalizeForDisplayTimezone(s, true)
      : FormatService.normalizeToUtc(s);
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  /** Parses API timestamps as a UTC instant (for comparisons and IST formatting). */
  parseToDate(value: string | number, assumeIstWhenNoTimezone = false): Date | null {
    return FormatService.parseApiTimestamp(value, assumeIstWhenNoTimezone);
  }

  /**
   * Formats an ISO 8601 date string to a readable format in IST: "DD-MMM-YYYY HH:mm"
   * Converts from UTC/ISO to Indian Standard Time (Asia/Kolkata).
   * Treats timezone-less strings (e.g. "2026-02-03T11:09:22.330111") as UTC by default.
   * @param assumeIstWhenNoTimezone When true, timezone-less values are treated as IST (policy expiry).
   * @param dateString - The date string to format (ISO 8601, timestamp, etc.)
   * @returns Formatted date string in IST, format "DD-MMM-YYYY HH:mm"
   * @example
   * formatDate('2026-02-03T11:09:22.330111') // Returns "03-Feb-2026 16:39" (IST)
   */
  /**
   * Discussion list / card activity line: relative wording when recent, otherwise
   * {@link formatDate} (IST). API values like "2026-04-10T10:39:30" without a timezone
   * are treated as UTC (same as {@link formatDate}).
   */
  formatDiscussionActivity(dateString: string): string {
    if (!dateString || typeof dateString !== 'string') return 'Unknown';
    const trimmed = dateString.trim();
    if (!trimmed) return 'Unknown';
    const normalized = FormatService.normalizeToUtc(trimmed);
    const dateObj = new Date(normalized);
    if (isNaN(dateObj.getTime())) return trimmed;

    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return this.formatDate(trimmed);
  }

  formatDate(dateString: string | number, assumeIstWhenNoTimezone = false): string {
    if (dateString === null || dateString === undefined || dateString === '') return '';
    try {
      const date = FormatService.parseApiTimestamp(dateString, assumeIstWhenNoTimezone);
      if (!date) {
        return typeof dateString === 'string' ? dateString : String(dateString);
      }
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: FormatService.IST_TIMEZONE,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const parts = formatter.formatToParts(date);
      const day = parts.find((p) => p.type === 'day')?.value ?? '';
      const month = parts.find((p) => p.type === 'month')?.value ?? '';
      const year = parts.find((p) => p.type === 'year')?.value ?? '';
      const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
      const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
      return `${day}-${month}-${year} ${hour}:${minute}`;
    } catch {
      return typeof dateString === 'string' ? dateString : String(dateString);
    }
  }

  /**
   * Formats a date string in Indian Standard Time (IST): "DD-MMM-YYYY HH:mm IST"
   * Same as formatDate but appends " IST" suffix for explicit timezone display.
   * @param dateString - The date string to format (ISO 8601, etc.)
   * @returns Formatted date string in IST with " IST" suffix
   */
  formatDateInIST(dateString: string | number, assumeIstWhenNoTimezone = false): string {
    const formatted = this.formatDate(dateString, assumeIstWhenNoTimezone);
    return formatted ? `${formatted} IST` : '';
  }

  /**
   * Formats a datetime for admin tables: "DD-MMM-YYYY h:mm AM/PM IST" (e.g. "13-Jun-2026 12:00 AM IST").
   * @param assumeIstWhenNoTimezone When true, timezone-less values are treated as IST (schedule dates).
   *   When false, they are treated as UTC (typical API created_at timestamps).
   */
  formatDateTime12hIST(
    dateString: string | number,
    assumeIstWhenNoTimezone = false,
  ): string {
    const parts = this.formatDateTime12hISTParts(dateString, assumeIstWhenNoTimezone);
    if (!parts) {
      return typeof dateString === 'string' ? dateString : String(dateString ?? '');
    }
    return `${parts.date} ${parts.time}`;
  }

  /**
   * Splits a datetime into date and time lines for compact table cells.
   * @returns e.g. { date: "09-Jun-2026", time: "8:30 PM IST" }
   */
  formatDateTime12hISTParts(
    dateString: string | number,
    assumeIstWhenNoTimezone = false,
  ): { date: string; time: string } | null {
    if (dateString === null || dateString === undefined || dateString === '') return null;
    try {
      const raw = typeof dateString === 'number' ? String(dateString) : dateString.trim();
      if (!raw) return null;
      const normalized = FormatService.normalizeForDisplayTimezone(raw, assumeIstWhenNoTimezone);
      const date = new Date(normalized);
      if (Number.isNaN(date.getTime())) return null;
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: FormatService.IST_TIMEZONE,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
      const parts = formatter.formatToParts(date);
      const day = parts.find((p) => p.type === 'day')?.value ?? '';
      const month = parts.find((p) => p.type === 'month')?.value ?? '';
      const year = parts.find((p) => p.type === 'year')?.value ?? '';
      const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
      const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
      const dayPeriod = (parts.find((p) => p.type === 'dayPeriod')?.value ?? '').toUpperCase();
      return {
        date: `${day}-${month}-${year}`,
        time: `${hour}:${minute} ${dayPeriod} IST`.trim(),
      };
    } catch {
      return null;
    }
  }

  /** Normalizes API date strings for display; UTC vs IST based on field semantics. */
  private static normalizeForDisplayTimezone(value: string, assumeIst: boolean): string {
    const s = value.trim();
    if (!s) return s;
    if (/[Zz]$|[+-]\d{2}:?\d{2}$/.test(s)) return s.replace(' ', 'T');
    const withT = s.includes('T') ? s : s.replace(' ', 'T');
    const base = withT.replace(/\.\d+$/, '');
    return assumeIst ? `${base}+05:30` : `${base}Z`;
  }

  /**
   * Formats a date to show only the date part in IST: "DD-MMM-YYYY"
   * Converts from UTC/ISO to Indian Standard Time (Asia/Kolkata).
   * @param dateString - The date string to format (ISO 8601, etc.)
   * @returns Formatted date string without time, in IST
   */
  formatDateOnly(dateString: string | number): string {
    if (dateString === null || dateString === undefined || dateString === '') return '';
    try {
      const date = FormatService.parseApiTimestamp(dateString);
      if (!date) {
        return typeof dateString === 'string' ? dateString : String(dateString);
      }
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: FormatService.IST_TIMEZONE,
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      const parts = formatter.formatToParts(date);
      const day = parts.find((p) => p.type === 'day')?.value ?? '';
      const month = parts.find((p) => p.type === 'month')?.value ?? '';
      const year = parts.find((p) => p.type === 'year')?.value ?? '';
      return `${day}-${month}-${year}`;
    } catch {
      return typeof dateString === 'string' ? dateString : String(dateString);
    }
  }

  /**
   * Formats a date to show only the time part in IST: "HH:mm"
   * Converts from UTC/ISO to Indian Standard Time (Asia/Kolkata).
   * @param dateString - The date string to format (ISO 8601, etc.)
   * @returns Formatted time string in IST
   */
  formatTimeOnly(dateString: string | number): string {
    if (dateString === null || dateString === undefined || dateString === '') return '';
    try {
      const date = FormatService.parseApiTimestamp(dateString);
      if (!date) {
        return '';
      }
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: FormatService.IST_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const parts = formatter.formatToParts(date);
      const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
      const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
      return `${hour}:${minute}`;
    } catch {
      return '';
    }
  }

  /**
   * Formats a date to card display format: "MMM DD, YYYY" (e.g., "Jun 22, 2025")
   * @param dateString - The date string to format
   * @returns Formatted date string in format "MMM DD, YYYY"
   * @example
   * formatDateForCard('2025-06-22T18:29:00Z') // Returns "Jun 22, 2025"
   */
  formatDateForCard(dateString: string | Date): string {
    if (!dateString) return '';
    try {
      const date =
        typeof dateString === 'string'
          ? new Date(FormatService.normalizeToUtc(dateString))
          : dateString;
      const month = date.toLocaleString('en-US', { month: 'short' });
      const day = date.getDate();
      const year = date.getFullYear();
      return `${month} ${day}, ${year}`;
    } catch (error) {
      return '';
    }
  }

  /**
   * Formats a date to model card display format: "MMM DD, YYYY" (e.g., "Dec 15, 2024")
   * @param dateString - The date string to format
   * @returns Formatted date string in format "MMM DD, YYYY"
   * @example
   * formatDateForModelCard('2024-12-15T18:29:00Z') // Returns "Dec 15, 2024"
   */
  formatDateForModelCard(dateString: string | Date): string {
    return this.formatDateForCard(dateString);
  }

  /**
   * Strips all HTML tags from a string and returns plain text.
   * Use in editors when you want to block HTML (e.g. on paste or before save).
   * @param html - String that may contain HTML
   * @returns Plain text with tags removed; entities like &amp; are decoded
   */
  stripAllHtml(html: string): string {
    if (!html || typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent ?? div.innerText ?? '').trim();
  }

  /**
   * Returns true if the string contains any HTML tags (e.g. &lt;p&gt;, &lt;script&gt;).
   * Use to validate editor content and show "HTML not allowed" when true.
   * @param text - String to check
   */
  containsHtmlTags(text: string): boolean {
    if (!text || typeof text !== 'string') return false;
    return /<[a-zA-Z][^>]*>/i.test(text);
  }

  /**
   * Converts a string to PascalCase format
   * Handles UPPERCASE, lowercase, SNAKE_CASE, and camelCase inputs
   * @param str - The string to convert
   * @returns PascalCase string
   * @example
   * toPascalCase('DATABANK') // Returns "Databank"
   * toPascalCase('CREATE') // Returns "Create"
   * toPascalCase('data_bank') // Returns "DataBank"
   * toPascalCase('dataBank') // Returns "DataBank"
   */
  toPascalCase(str: string): string {
    if (!str) return '';
    
    // Remove "adex:" prefix if present
    let cleaned = str.replace(/^adex:/i, '').trim();
    if (!cleaned) return '';
    
    // If already in proper format (mixed case), return as is
    if (cleaned !== cleaned.toUpperCase() && cleaned !== cleaned.toLowerCase()) {
      // Handle camelCase/PascalCase: "DataBank" -> "DataBank"
      return cleaned;
    }
    
    // Handle UPPERCASE or lowercase
    if (cleaned === cleaned.toUpperCase() || cleaned === cleaned.toLowerCase()) {
      // Split by underscore if SNAKE_CASE
      if (cleaned.includes('_')) {
        return cleaned
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join('');
      }
      // Single word: uppercase first letter, lowercase rest
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
    }
    
    return cleaned;
  }

  /**
   * Formats asset type names with specific mappings
   * Maps common asset type names to their display names
   * @param assetType - The asset type string to format
   * @returns Formatted asset type name
   * @example
   * formatAssetType('DATABANK') // Returns "Dataset"
   * formatAssetType('databank') // Returns "Dataset"
   * formatAssetType('MODEL') // Returns "Model"
   */
  formatAssetType(assetType: string): string {
    if (!assetType) return '';
    
    // Convert to string and handle null/undefined
    const strValue = String(assetType).trim();
    if (!strValue) return '';
    
    // Normalize by removing spaces, underscores, and hyphens for comparison
    // This handles: "Data Bank", "Data bank", "DATA BANK", "DataBank", "DATA_BANK", "DATA-BANK" -> all become "DATABANK"
    const normalized = strValue.replace(/[\s_-]/g, '').toUpperCase();
    
    // Check for Data Bank variations explicitly (case-insensitive, with or without spaces)
    const upperType = strValue.toUpperCase().trim();
    if (upperType === 'DATA BANK' || upperType === 'DATABANK' || upperType === 'DATA_BANK' || upperType === 'DATA-BANK') {
      return 'Dataset';
    }
    
    // Also check the normalized version (handles "Data bank", "Data Bank", "DataBank", etc.)
    if (normalized === 'DATABANK') {
      return 'Dataset';
    }
    // Check if it contains DATABANK (in case of prefixes/suffixes like "adex:DATABANK")
    if (normalized.includes('DATABANK')) {
      return 'Dataset';
    }
    
    // Handle other asset types using normalized comparison
    // .includes() handles namespace-prefixed values like "adex:AiModel" → normalized "ADEX:AIMODEL"
    if (normalized === 'AIMODEL' || normalized.includes('AIMODEL')) {
      return 'Model';
    }
    if (normalized === 'MODEL' || normalized.includes('MODEL')) {
      return 'Model';
    }
    if (normalized === 'USECASE' || normalized.includes('USECASE')) {
      return 'Use Case';
    }
    
    // If no mapping found, format using toPascalCase
    return this.toPascalCase(strValue);
  }

  /**
   * Formats and filters roles to only show defined roles from the roles service
   * Removes system roles like "default-roles-tgdex-pilot" and "offline_access"
   * Maps role aliases to standard roles (e.g., "cos_admin" -> "admin")
   * @param roles - Array of role strings to filter and format
   * @returns Array of filtered and formatted role strings
   * @example
   * formatRoles(['consumer', 'cos_admin', 'default-roles-tgdex-pilot', 'offline_access'])
   * // Returns ['consumer', 'admin']
   */
  formatRoles(roles: string[]): string[] {
    if (!roles || !Array.isArray(roles) || roles.length === 0) {
      return [];
    }

    // Define valid roles from the Role enum
    const validRoles = Object.values(Role);
    
    // Define system roles to exclude
    const systemRolesToExclude = [
      'default-roles-tgdex-pilot',
      'offline_access',
      'uma_authorization',
      'default-roles-iudx-v2'
    ];

    // Filter and map roles
    const filteredRoles = roles
      .map(role => {
        // Normalize role: convert to lowercase and replace underscores with hyphens
        const normalizedRole = role.toLowerCase().replace(/_/g, '-');
        
        // Check if this role has an alias (e.g., "cos-admin" -> "admin")
        if (ROLE_ALIASES[normalizedRole]) {
          return ROLE_ALIASES[normalizedRole];
        }
        
        // Return normalized role if it's a valid role
        return normalizedRole;
      })
      .filter(role => {
        // Only include valid roles and exclude system roles
        return validRoles.includes(role as Role) && !systemRolesToExclude.includes(role);
      })
      // Remove duplicates
      .filter((role, index, self) => self.indexOf(role) === index)
      // Sort roles by priority (admin, org-admin, provider, consumer)
      .sort((a, b) => {
        const priority: Record<string, number> = {
          [Role.ADMIN]: 4,
          [Role.ORG_ADMIN]: 3,
          [Role.PROVIDER]: 2,
          [Role.CONSUMER]: 1,
        };
        return (priority[b] || 0) - (priority[a] || 0);
      })
      // Convert to PascalCase
      .map(role => {
        // Handle hyphenated roles (e.g., "org-admin" -> "Org Admin")
        if (role.includes('-')) {
          return role
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }
        // Single word: capitalize first letter
        return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
      });

    return filteredRoles;
  }

  /**
   * Returns the highest-priority role from the given roles array.
   * Role order: Admin > Org Admin > Provider > Consumer.
   * @param roles - Array of role strings
   * @returns The highest role as a formatted string, or empty string if none
   */
  getHighestRole(roles: string[]): string {
    const formatted = this.formatRoles(roles || []);
    return formatted.length > 0 ? formatted[0] : '';
  }

  /**
   * Formats a date range for API calls (ISO format: YYYY-MM-DDTHH:mm:ss)
   * Sets start date to beginning of day (00:00:00) and end date to end of day (23:59:59)
   * @param dateRange - The date range object with startDate and endDate (can be Date | null)
   * @returns Object with startDate and endDate as ISO strings, or undefined if dateRange is invalid
   * @example
   * formatDateRangeForApi({ startDate: new Date('2025-12-26'), endDate: new Date('2025-12-31') })
   * // Returns { startDate: '2025-12-26T00:00:00', endDate: '2025-12-31T23:59:59' }
   */
  formatDateRangeForApi(dateRange: { startDate: string | Date | null; endDate: string | Date | null } | null | undefined): { startDate: string; endDate: string } | undefined {
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
      return undefined;
    }

    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    
    // Set time to start of day (00:00:00) for start date
    start.setHours(0, 0, 0, 0);
    // Set time to end of day (23:59:59) for end date
    end.setHours(23, 59, 59, 999);
    
    // Format as ISO string (YYYY-MM-DDTHH:mm:ss)
    const startDate = start.toISOString().slice(0, 19);
    const endDate = end.toISOString().slice(0, 19);
    
    return { startDate, endDate };
  }

  /**
   * Permission display label for UI only: "Open" -> "Public", "Restricted" -> "Restricted".
   * API continues to use OPEN / RESTRICTED.
   */
  getPermissionDisplayLabel(permission: string | null | undefined): string {
    if (!permission) return '';
    return permission === 'Open' ? 'Public' : permission;
  }

  /**
   * Catalogue API license: when the user selects "Other", persist the custom Specify License text.
   */
  static resolveCatalogueLicenseForApi(
    license?: string | null,
    otherLicense?: string | null,
    fallback = '',
  ): string {
    const lic = String(license ?? '').trim();
    const other = String(otherLicense ?? '').trim();
    if (lic.toLowerCase() === 'other') {
      return other || lic || fallback;
    }
    return lic || fallback;
  }

  /** Detail pages: show custom license text (supports legacy API rows with license=Other + otherLicense). */
  static resolveCatalogueLicenseDisplay(
    license?: string | null,
    otherLicense?: string | null,
  ): string {
    return FormatService.resolveCatalogueLicenseForApi(license, otherLicense, '');
  }

  resolveCatalogueLicenseForApi(
    license?: string | null,
    otherLicense?: string | null,
    fallback = '',
  ): string {
    return FormatService.resolveCatalogueLicenseForApi(license, otherLicense, fallback);
  }

  resolveCatalogueLicenseDisplay(
    license?: string | null,
    otherLicense?: string | null,
  ): string {
    return FormatService.resolveCatalogueLicenseDisplay(license, otherLicense);
  }
}
